import { Injectable } from '@angular/core';
import { ServerApi } from './server.api';
import { EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { WebshareCloudLoginComponent } from 'src/app/common/Forms/Dialogs/webshare-cloud-login/webshare-cloud-login.component';
import { DialogType, DialogService } from '../dialogs.service';
import { DialogRef } from 'src/app/common/Forms/Dialog-types/dialog-ref';
import { DialogModel } from 'src/app/common/Models/Dialog/dialog.model';
import { WebshareLoginApiSvc } from './webshare.login.api.svc';
import { isNullOrUndefined } from '../../helper';
import { InputsBindingsModel } from 'src/app/common/Models/Dialog/inputs-binding.model';
import { StatusService, MessagesBank } from '../status.service';
import { SessionApiSvc } from './session.api.svc';
import { ICompanyInfo, IntositeLoginMethod } from 'src/app/common/Models/UI/company-info.interface';
import { ISite } from 'src/app/Store/sites.state/sites.model';
import { Store } from '@ngxs/store';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ApiTools } from './api.tools';
import { RemovePointCloudSession, SetCompanyInfo, SetPointCloudSession } from 'src/app/Store/app.state/app.actions';
import { NotificationDialogComponent } from 'src/app/common/Forms/Dialogs/notification-dialog/notification-dialog.component';
import { ButtonInfo, ValueAndDisplay } from 'src/app/common/UI-Components/helperClasses/value-and-display.class';
import { ILayer, IScanProjectProperties } from 'src/app/Store/layers.state/layers.model';
import { CmdRouterService, CMD_ACTIONS, CMD_TARGETS } from '../cmd-router.service';
import { SiemensAnalyticsService } from '../siemens-analytics.service';
import { IPlacemark } from 'src/app/Store/placemarks.state/placemarks.model';
import { IProject } from 'src/app/common/Forms/Dialogs/import-scans-dialog/import-scans-util';
import { IPointCloudSession, IWebShareConf, WebshareAuthType, WebshareLoadImageMethod } from 'src/app/common/Models/UI/point-cloud.interface';
import { WebshareConfDialogComponent } from 'src/app/common/Forms/Dialogs/webshare-conf-dialog/webshare-conf-dialog.component';
import { SCENE_MODE } from 'src/app/Store/app.state/app.model';
import * as THREE from 'three';
import { AppState } from 'src/app/Store/app.state/app.state';

@Injectable()
export class WebshareApiSvc {
  private readonly webShareLoginInfo = new Subject<any>();
  private webshareRequestSub = new Subscription();

  public companyInfo: ICompanyInfo = null;
  companyInfo$: Observable<ICompanyInfo>;

  constructor(private readonly serverApi: ServerApi,
    private readonly dialogSrv: DialogService,
    private readonly webshareLoginApiSvc: WebshareLoginApiSvc,
    private readonly statusBar: StatusService,
    private readonly sessionApiSvc: SessionApiSvc,
    private readonly store: Store,
    private readonly cmdRouterSvc: CmdRouterService,
    private readonly siemensAnalyticsService: SiemensAnalyticsService,
    private readonly dialogService: DialogService) {
    this.companyInfo$ = this.store.select(AppState.getCompanyInfo);
    this.companyInfo$.subscribe( (companyInfo: ICompanyInfo) => {
        this.companyInfo = companyInfo;
      });
    }

    public pointCloudConfsCreated(object: any, throughSse: boolean): void {
      console.log('SSE GOT [PointCloudConf] created', object);
    }

    public pointCloudConfsDeleted(object: any, throughSse: boolean): void {
      console.log('SSE GOT [PointCloudConf] deleted', object);
    }

  public pointCloudConfsUpdated(object: any, throughSse: boolean): void {
    const updatedConfs: IWebShareConf[] = throughSse ? object.webShareConfDataList : object;
    let activeSite: ISite = this.store.selectSnapshot<ISite>((state: any) => state.StateApp.activeSite );
    if (activeSite.pointCloudConfId) {
      let updatedConf: IWebShareConf = updatedConfs.find( c => c.id == activeSite.pointCloudConfId);
      if (updatedConf) {
        let existingConfs = this.companyInfo.webShareConfs || [];
        let existingConf: IWebShareConf = existingConfs.find( c => c.id == activeSite.pointCloudConfId);
        if (existingConf && ( updatedConf.loginMethod != existingConf.loginMethod ||  updatedConf.browserDomainUrl != existingConf.browserDomainUrl ||
            updatedConf.domainUrl != existingConf.domainUrl || updatedConf.apiKey != existingConf.apiKey)) {
          this.webshareConfUpdatedForActiveSite(throughSse);
        }
      } else {
        this.webshareConfUpdatedForActiveSite(throughSse);
      }
    }
    this.companyInfo.webShareConfs = updatedConfs;
    this.store.dispatch(new SetCompanyInfo(this.companyInfo));
  }

  public webshareConfUpdatedForActiveSite(throughSse: boolean): void {
    if (throughSse) {
      const inputsBinding: InputsBindingsModel = new Map([
        ['type', 'warning'],
        ['title', 'Point Cloud Configuration Changes'],
        ['message', `The administrator has changed the point cloud configuration method in your site.
        You must login again for the modifications to become active.<br/> Press Continue to return to Login.`]
      ]);
      const dialog: DialogRef = this.dialogSrv.createNotificationDialog(inputsBinding);
      const dialogComp: NotificationDialogComponent = (dialog.instance as NotificationDialogComponent);

      dialogComp.buttonsInfo = [
        new ButtonInfo('ok', 'Continue'),
      ];
      dialog.onClose$().subscribe((model: DialogModel) => {
        if (model.userAction === 'ok') {
          this.sessionApiSvc.closeSession();
        }
      });
    } else {
      this.clearPointCloudSession();
    }
  }

  public pointCloudLoginInfoInternal(): Observable<any> {
    return this.webShareLoginInfo.asObservable();
  }

  public getProjects(): Observable<any> {
    const {domain, domainhost} = this.webshareLoginApiSvc.getPointCloudDomainFromUrl();
    const getProjectsUrl: string = `${environment.serverUrl}/newapi/external/${domain}/project?domainhost=${domainhost}`;
    return this.serverApi.sendGetToServer(getProjectsUrl).pipe(
      map((projects: any[]) => {
        return projects.map(project => {
          project.id = project.Name; //Name is Unique in case of Webshare.. So using it as ID
          project.name = project.Name;
          project.displayName = project.DisplayName;
          project.description = project.Description;
          project.modifiedAt = project.UploadDate;
          return project;
        });
      })
    );
  }

  public getScans(project: IProject): Observable<any> {
    const {domain, domainhost} = this.webshareLoginApiSvc.getPointCloudDomainFromUrl();
    const getScansUrl: string = `${environment.serverUrl}/newapi/external/${domain}/scan/${project.name}?domainhost=${domainhost}`;
    return this.serverApi.sendGetToServer(getScansUrl).pipe(
      map((scans: any[]) => {
        return scans.map(scan => {
          scan.id = scan.UUID;
          scan.name = scan.Name;
          scan.modifiedAt = scan.ModificationTime;
          scan.createdAt = scan.RecordingTime;
          scan.x = scan.TransformationGlobal[12];
          scan.y = scan.TransformationGlobal[13];
          scan.z = scan.TransformationGlobal[14];
          return scan;
        });
      })
    );
  }

  public async getPointCloudId(selectedProject: any): Promise<any> {
    try {
      const {domain, domainhost} = this.webshareLoginApiSvc.getPointCloudDomainFromUrl();
      const getScansUrl: string = `${environment.serverUrl}/newapi/external/${domain}/pointcloud/${selectedProject.name}?domainhost=${domainhost}`;
      const result = await this.serverApi.sendGetToServer(getScansUrl).toPromise();
      return result[0]?.UUID || null;
    } catch(err) {
      this.statusBar.removeStatus(MessagesBank.IMPORTING_SCAN);
      this.serverApi.createNotifiactionDialogForHttpCrisis(err, 'Error in getting Point Cloud. Please try later');
    }
  }

  public getWebShareScanProjectDetails(projectName: string, scanId: string): Observable<any>{
    const {domain, domainhost}  = this.webshareLoginApiSvc.getPointCloudDomainFromUrl();
    const url: string = `${environment.serverUrl}/newapi/external/${domain}/scan/${projectName}/${scanId}?domainhost=${domainhost}`;
    return this.serverApi.sendGetToServer(url);
  }

  public async loginToPointCloud(testWebshareConf?: IWebShareConf, tourSite?: ISite): Promise<any> {
    const testConnection = !!testWebshareConf;
    let activeSite: ISite = tourSite ?? this.store.selectSnapshot<ISite>((state: any) => state.StateApp.activeSite );
    if (testConnection) {
      this.handleWebshareAppPasswordLogin(testWebshareConf, testConnection);
    } else {
      let companyInfo: ICompanyInfo = this.store.selectSnapshot<ICompanyInfo>((state: any) => state.StateApp.companyInfo );     
      let webshareConf = this.getWebshareConfById(activeSite.pointCloudConfId);

      if (webshareConf) {
        this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.PANORAMIC_MANAGER, CMD_ACTIONS.SET_POINTCLOUD_PARAMS, { webshareSiteAuthType: webshareConf.loginMethod, webshareLoadImageMethod: webshareConf.loadImageMethod });
        switch (webshareConf.loginMethod) {
          case WebshareAuthType.Manual:
            this.handleWebshareManualLogin(activeSite.id, webshareConf);
            break;
          case WebshareAuthType.Automatic:
            this.handleWebshareAppPasswordLogin(webshareConf, testConnection);
            break;
          case WebshareAuthType.Enterprise:
            if (companyInfo.intositeLoginMethod != IntositeLoginMethod.Enterprise) {
              this.serverApi.createNotifiactionDialogForHttpCrisis(new Error(), 'Login to Intosite with Enterprise, in order to use Enterprise for point cloud authentication.', false, 'Connection Status');
              this.webShareLoginInfo.next(false);
            } else {
              this.handleWebshareEnterpriseLogin(webshareConf);
            }
            break;
          case WebshareAuthType.Sphere:
            if (companyInfo.intositeLoginMethod != IntositeLoginMethod.Sphere) {
              this.serverApi.createNotifiactionDialogForHttpCrisis(new Error(), 'Login to Intosite with Sphere, in order to use Sphere for point cloud authentication.', false, 'Connection Status');
              this.webShareLoginInfo.next(false);
            } else {
              this.handleWebshareSphereLogin(webshareConf);
            }
            break;
        }
      } else {
        this.handleWebshareManualLogin(activeSite.id);
      }
    }
  }

  public handleWebshareAppPasswordLogin(webshareConf, testConnection) {
    if (!ApiTools.isValidUrl(webshareConf.domainUrl)) {
      let error = new Error('Connection failed - The domain is not valid.');
      this.serverApi.createNotifiactionDialogForHttpCrisis(error, 'Connection failed - The domain is not valid.', false, 'Connection Status');
      this.webShareLoginInfo.next(false);
      return;
    }

    let pointCloudSession: IPointCloudSession = this.store.selectSnapshot<IPointCloudSession>((state: any) => state.StateApp.pointCloudSession );
    if (testConnection || isNullOrUndefined(pointCloudSession)) {
          this.performWebShareAppPasswordLogin(webshareConf, testConnection);
    } else {
      this.webshareLoginApiSvc.checkWebshareLogin(webshareConf.domainUrl)
        .subscribe({
          next: () => {
            this.webShareLoginInfo.next(true);
          },
          error: () => {
            this.performWebShareAppPasswordLogin(webshareConf, testConnection);
          }
        })
    }
  }

  public handleWebshareManualLogin(siteId: number, webshareConf: IWebShareConf = null) {
    let storedPointCloudSession: IPointCloudSession = this.store.selectSnapshot<IPointCloudSession>((state: any) => state.StateApp.pointCloudSession );
    if (isNullOrUndefined(storedPointCloudSession)) {
      this.performWebShareManualLogin(siteId, webshareConf);
    } else {
      this.webshareLoginApiSvc.checkWebshareLogin(storedPointCloudSession.domainUrl)
        .subscribe({
          next: () => {
            this.webShareLoginInfo.next(true);
          },
          error: () => {
            this.performWebShareManualLogin(siteId, webshareConf);
          }
        });
    }
  }

  public handleWebshareEnterpriseLogin(webshareConf: IWebShareConf) {
    if (!ApiTools.isValidUrl(webshareConf.domainUrl)) {
      let error = new Error('Connection failed - The domain is not valid.');
      this.serverApi.createNotifiactionDialogForHttpCrisis(error, 'Connection failed - The domain is not valid.', false, 'Connection Status');
      this.webShareLoginInfo.next(false);
      return;
    }
    this.setPointCloudSession(webshareConf);
    if (webshareConf.loadImageMethod && (webshareConf.loadImageMethod === WebshareLoadImageMethod.Client || webshareConf.loadImageMethod === WebshareLoadImageMethod.Auto)) {
      this.getWebShareAuthData().subscribe({
        next: (res: any) => {
          this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.PANORAMIC_MANAGER, CMD_ACTIONS.SET_POINTCLOUD_PARAMS, { webshareSiteAuthType: WebshareAuthType.Enterprise, webshareLoadImageMethod: webshareConf.loadImageMethod, webshareAuthToken: res.token, webshareApiKey: res.apiKey });
          this.webShareLoginInfo.next(true);
        },
        error: (err) => {
          console.log(`Error while getting the Webshare Auth Data : ${err}`);
          this.webShareLoginInfo.next(false);
        }
      });
    } else {
      this.webShareLoginInfo.next(true);
    }
  }

  public handleWebshareSphereLogin(webshareConf: IWebShareConf) {
    if (!ApiTools.isValidUrl(webshareConf.domainUrl)) {
      let error = new Error('Connection failed - The domain is not valid.');
      this.serverApi.createNotifiactionDialogForHttpCrisis(error, 'Connection failed - The domain is not valid.', false, 'Connection Status');
      this.webShareLoginInfo.next(false);
      return;
    }
    this.setPointCloudSession(webshareConf);
    if (webshareConf.loadImageMethod && (webshareConf.loadImageMethod === WebshareLoadImageMethod.Client || webshareConf.loadImageMethod === WebshareLoadImageMethod.Auto)) {
      this.getWebShareAuthData().subscribe({
        next: (res: any) => {
          this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.PANORAMIC_MANAGER, CMD_ACTIONS.SET_POINTCLOUD_PARAMS, { webshareSiteAuthType: WebshareAuthType.Sphere, webshareLoadImageMethod: webshareConf.loadImageMethod, webshareAuthToken: res.token, webshareApiKey: res.apiKey });
          this.webShareLoginInfo.next(true);
        },
        error: (err) => {
          console.log(`Error while getting the Webshare Auth Data : ${err}`);
          this.webShareLoginInfo.next(false);
        }
      });
    } else {
      this.webShareLoginInfo.next(true);
    }
  }

  public getWebShareAuthData(): Observable<any> {
    const url: string = `${environment.baseServerUrl}/newapi/webshareauthdata`;
    return this.serverApi.sendGetToServer(url);
  }

  public async performWebShareManualLogin(siteId: number, webshareConf: IWebShareConf = null): Promise<any> {
    const userDetails: any = await this.webshareLoginApiSvc.getScanProjectPartnerDetails(siteId);
    if (webshareConf?.domainUrl) {
      if (webshareConf.domainUrl != userDetails.domainUrl) {
        userDetails.userName = "";
        userDetails.password = "";
      }
    } else {
      webshareConf = {
        companyId: null,
        name: "",
        browserDomainUrl: "",
        domainUrl: userDetails.domainUrl,
        loginMethod: WebshareAuthType.Manual,
        loadImageMethod: ""
      }
    }

    if (userDetails) {
      const inputsBinding: InputsBindingsModel = new Map();
      inputsBinding.set('userName', userDetails.userName);
      inputsBinding.set('password', userDetails.password);
      inputsBinding.set('webshareConf', webshareConf);
      inputsBinding.set('siteId', siteId)

      const dialog: DialogRef = this.dialogSrv.createDialog(WebshareCloudLoginComponent, DialogType.Modal, inputsBinding, null, 400, 380);

      dialog.onClose$().subscribe(async (model: DialogModel) => {
        if (model.userAction === 'continue') {
          this.webShareLoginInfo.next(true);
        } else {
          this.webShareLoginInfo.next(false);
        }
      });
    }
  }

  public performWebShareAppPasswordLogin(webshareConf: IWebShareConf, testConnection: boolean) {
    const STATUS_MESSAGE = testConnection ? MessagesBank.POINT_CLOUD_CHECK_CONNECTION_INPROGRESS : MessagesBank.POINTCLOUD_LOGIN_INPROGRESS;
    this.statusBar.addNewStatus(STATUS_MESSAGE);

    const appPasswordLoginRequest = this.webshareLoginApiSvc.getWebsharePassword(webshareConf.domainUrl, webshareConf.apiKey)
      .pipe(catchError( (error: HttpErrorResponse) => {
        this.webShareLoginInfo.next(false);
        this.statusBar.removeStatus(STATUS_MESSAGE);
        return EMPTY;
      }))
      .pipe(
        switchMap((res: any) => {
          return this.webshareLoginApiSvc.performWebshareLogin(res.username, atob(res.Password64), webshareConf.domainUrl)
        })
    );

    this.webshareRequestSub = appPasswordLoginRequest.subscribe({
      next: () => {
        if (!testConnection) {
          this.setPointCloudSession(webshareConf);
        }
        this.statusBar.removeStatus(STATUS_MESSAGE);
        this.webShareLoginInfo.next(true);
      },
      error: (err) => {
        if ( err.status === 401 ) {
          this.statusBar.removeStatus(STATUS_MESSAGE);
          this.webShareLoginInfo.next(false);
          this.serverApi.createNotifiactionDialogForHttpCrisis(err, 'Some details are incorrect. Try again.', false, 'Connection Status');
        } else if ( err.status === 404 ) {
          this.statusBar.removeStatus(STATUS_MESSAGE);
          this.webShareLoginInfo.next(false);
          this.serverApi.createNotifiactionDialogForHttpCrisis(err, 'The server encountered a problem. Please contact your system administrator.');
        } else {
          this.webshareLoginApiSvc.checkWebshareLogin(webshareConf.domainUrl)
            .subscribe({
              next: () => {
                if (!testConnection) {
                  this.setPointCloudSession(webshareConf);
                }
                this.statusBar.removeStatus(STATUS_MESSAGE);
                this.webShareLoginInfo.next(true);
              },
              error: (err) => {
                this.statusBar.removeStatus(STATUS_MESSAGE);
                this.webShareLoginInfo.next(false);
                this.serverApi.createNotifiactionDialogForHttpCrisis(err, 'The server encountered a problem. Please contact your system administrator.');
              }
            });
        }
      }
    });
  }

  public cancelCheckConnection(): void {
    this.webshareRequestSub.unsubscribe();
    this.statusBar.removeStatus(MessagesBank.POINT_CLOUD_CHECK_CONNECTION_INPROGRESS);
  }

  public async showPointCloud(sceneMode: SCENE_MODE, scanProjectProperties: IScanProjectProperties, scanId: string, phi: number, theta: number, fov: number) {
    const placemarks: IPlacemark[] = this.store.selectSnapshot<IPlacemark[]>((state: any) => state.StatePlacemarks.placemarks);
    const placemark: IPlacemark = placemarks.find( pm => pm.scanId == scanId);
    if (!scanProjectProperties) {
      const layers: ILayer[] = this.store.selectSnapshot<ILayer[]>((state: any) => state.StateLayers.layers);
      const currentLayer = layers.find((foundLayer: ILayer) => foundLayer.id === placemark.parentLayerId);
      scanProjectProperties = currentLayer.scanProjectProperties;
    }
    if (scanProjectProperties.pointCloudId) {
      try {
        this.statusBar.addNewStatus(MessagesBank.OPENING_OPINTCLOUD);
        await this.checkUserAccessToProject(placemark.parentLayerId);
        this.getWebShareScanProjectDetails(scanProjectProperties.name, scanId)
          .subscribe( (res: any) => {
            if ( res.hasOwnProperty('TransformationGlobal') ) {
              const targetVector = new THREE.Vector3()
              targetVector.x = res['TransformationGlobal'][12];
              targetVector.z = res['TransformationGlobal'][13];
              targetVector.y = res['TransformationGlobal'][14];
              this.openPointCloud(fov, scanProjectProperties.pointCloudId, targetVector, phi, theta, scanProjectProperties.name);
            }
            this.statusBar.removeStatus(MessagesBank.OPENING_OPINTCLOUD);
        });
      } catch (error) {
        console.log(`Error in getting Webshare Scan Project Details`, error);
        this.statusBar.removeStatus(MessagesBank.OPENING_OPINTCLOUD);
        this.serverApi.createNotificationDialog("err", "Loading Error", "There was an internal error. Please contact the System Administrator.");
      }
    } else {
      this.serverApi.createNotificationDialog("warning", "Point Cloud is not available", `Point Cloud is not available for scan ${placemark.name} in Webshare.`);
    }
   }

  private openPointCloud(fov: number, pointCloudId: string, targetVector: THREE.Vector3, phi: number, theta: number, projectName: string) {
    const pointCloudSession: IPointCloudSession = this.store.selectSnapshot<IPointCloudSession>((state: any) => state.StateApp.pointCloudSession );
    if (pointCloudSession) {
      let url = (pointCloudSession.browserDomainUrl || pointCloudSession.domainUrl) + '/?v=pv&t=p:default,c:panoramaview,h:f,m:t&pv=pv1&pv1=vt:3d,u:'
      + pointCloudId
      + ','
      + 'cf:'
      + fov
      +','
      +'dh:f,st:f,'
      + 'cx:'
      + targetVector.x
      + ','
      + 'cy:'
      + targetVector.z // because y,z were inverted during creation of placemark!
      + ','
      + 'cz:'
      + targetVector.y
      + ','
      + 'p:' + phi + ',t:' + theta
      + '&p=p:'
      + projectName;
      window.open(url);
      // Log Siemens Analytics event
      this.siemensAnalyticsService.logEvent('INS_PointCloudAccess');
    } else {
      console.log("Webshare configuration not found while opening point cloud")
    }
  }

  public async checkUserAccessToProject(layerId: string, scanProjectProperties?: IScanProjectProperties): Promise<any> {
    return new Promise( (resolve, reject) => {
      let projectName: string = scanProjectProperties?.name;
      this.getProjects()
        .subscribe({
          next: (projects: any[]) => {
            if (!scanProjectProperties?.name) {
              const layers: ILayer[] = this.store.selectSnapshot<ILayer[]>((state: any) => state.StateLayers.layers);
              const currentLayer = layers.find((foundLayer: ILayer) => foundLayer.id === layerId);
              scanProjectProperties = currentLayer.scanProjectProperties;
              projectName = currentLayer.scanProjectProperties.name;
            }
            const project = projects.find( (obj: any) => obj.Name == projectName );
            if (project) {
              resolve(true);
            } else {
              const err = new Error(`You do not have access to the ${projectName} project in Webshare.`);
              this.serverApi.createNotifiactionDialogForHttpCrisis(err,`You do not have access to the ${projectName} project in Webshare.`);
              reject(err);
            }
          },
          error: (err) => {
            console.log(`Error in getting Webshare Projects`, err);
            reject(err instanceof Error ? err : new Error(`Error in getting Webshare Projects: ${err}`));
          }
        });
    });
  }

  public updateWebhshareConf(companyId: number, confs: IWebShareConf[]): Observable<IWebShareConf[]> {
    const url: string = `${environment.serverUrl}/newapi/webshare/confs?companyId=${companyId}&siteId=${ApiTools.defaultSiteId}`;
    return this.serverApi.sendPostToServer(url, confs);
  }

  public getWebshareConfById(confId: number): IWebShareConf {
      const pointCloudConfs = this.getAllPointCloudConfs();
      if (pointCloudConfs) {
          const webshareConf = pointCloudConfs.find( (conf: IWebShareConf) => conf.id == confId) || null;
          return webshareConf;
      } else {
          return null;
      }
  }

  public getScanPlacemarkUrl(scanProjectProperties: IScanProjectProperties, scanId: string): string {
    return `${scanProjectProperties?.url}/data/project/${scanProjectProperties?.url}/scan/${scanId}/color/8192`
  }

  public getResoultionOptions(): ButtonInfo[] {
    const resolutions: ButtonInfo[] = [
      new ButtonInfo("1024", 'Default Scan resolution is up to 1K'),
      new ButtonInfo("2048", 'Default Scan resolution is up to 2K'),
      new ButtonInfo("4096", 'Default Scan resolution is up to 4K'),
      new ButtonInfo("8192", 'Default Scan resolution is up to 8K'),
      new ButtonInfo("16384", 'Default Scan resolution is up to 16K'),
    ];
    return resolutions;
  }

  public getDefaultResolution(): string {
    return "4096";
  }

  public openWebPointCloudConfDialog(): void {
    this.dialogService.createDialog(WebshareConfDialogComponent, DialogType.Modal, null, null, 985);
    // Log Siemens Analytics event
    this.siemensAnalyticsService.logEvent('INS_PointCloudConfiguration', false);
  }

  public setPointCloudSession(pointCloudSession: IPointCloudSession): void {
    pointCloudSession.partnerName = 'FARO';
    localStorage.setItem('pointCloudSession', JSON.stringify(pointCloudSession));
    this.store.dispatch(new SetPointCloudSession(pointCloudSession));
  }

  public clearPointCloudSession(): void {
    this.store.dispatch(new RemovePointCloudSession());
    localStorage.removeItem('pointCloudSession');
  }

  public getAllPointCloudConfs(): IWebShareConf[] {
    return this.companyInfo.webShareConfs;
  }

  public getPointCloudConfsSelectionOpts(): ButtonInfo[] {
    let pointCloudConfs = this.companyInfo.webShareConfs || [];
    switch (this.companyInfo.intositeLoginMethod) {
      case IntositeLoginMethod.Enterprise:
        pointCloudConfs = pointCloudConfs.filter((c: IWebShareConf) => c.loginMethod != WebshareAuthType.Sphere);
        break;
      case IntositeLoginMethod.Sphere:
        pointCloudConfs = pointCloudConfs.filter((c: IWebShareConf) => c.loginMethod != WebshareAuthType.Enterprise);
        break;
      default:
        pointCloudConfs = pointCloudConfs.filter((c: IWebShareConf) => c.loginMethod != WebshareAuthType.Sphere && c.loginMethod != WebshareAuthType.Enterprise);
        break;
    }
    const pointCloudConfOptions = [];
    pointCloudConfs.forEach( (webshareConf: IWebShareConf) => pointCloudConfOptions.push(new ButtonInfo(webshareConf.id, webshareConf.name)));
    return pointCloudConfOptions;
  }

  public getPointCloudConfById(webshareConfId: number): IWebShareConf {
    const pointCloudConfs = this.companyInfo.webShareConfs || [];
    const webshareConf = pointCloudConfs.find( (conf: IWebShareConf) => conf.id == webshareConfId) || null;
    return webshareConf;
  }

  public showTestPointCloudConnection(pointCloudConf: IWebShareConf): boolean {
    if (pointCloudConf) {
      return (pointCloudConf.loginMethod === WebshareAuthType.Automatic && !!pointCloudConf.apiKey && !!pointCloudConf.domainUrl);
    }
    return false;
  }

  public async testPointCloudConnection(pointCloudConf: IWebShareConf): Promise<any> {
    return new Promise((resolve, reject) => {
      this.pointCloudLoginInfoInternal().pipe(take(1)).subscribe((success: boolean) => {
        if (success) {
          const inputsBinding: InputsBindingsModel = new Map<string, any>([
            [ 'type', 'info'],
            [ 'title', 'Connection Status' ],
            [ 'message', 'Succeeded in connecting to the point cloud hub.'],
            [ 'buttonsInfo', [new ValueAndDisplay('close', 'Close')]]
          ]);
          this.dialogService.createNotificationDialog(inputsBinding);
          resolve(success);
        } else {
          reject(new Error(`PointCloud login failed with Error: ${success}`));
        }
      });
      this.loginToPointCloud(pointCloudConf);
    });
  }

  public getPointCloudConfIdFromSite(site: any): number {
    return site.webShareConfId ?? 0;
  }

  public setPointCloudConfIdInSite(site: any): void {
    site.webShareConfId = site.pointCloudConfId;
    site.pointCloudConfId = null;
  }
}
